import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Copy, RefreshCw,
  X, Trash2, Check, AlertTriangle, Settings, Clock, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Format "HH:MM" (24h) → "h:MM AM/PM"
const fmtShiftTime = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${ampm}` : `${h12} ${ampm}`;
};

// ─── Date helpers ──────────────────────────────────────────────
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const parseISO = (s) => {
  // Treat the ISO date as local — no timezone shift surprises.
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const mondayOf = (d) => {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
};
const addDays = (d, n) => {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
};
const minutesBetween = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
};
const fmtHours = (mins) => `${(mins / 60).toFixed(1)}h`;

// ─── Shift editor modal ────────────────────────────────────────
function ShiftModal({ shift, staffList, locations, defaultDate, defaultUserId, onClose, onSaved, onDeleted }) {
  const isNew = !shift?.shift_id;
  const [form, setForm] = useState(() => ({
    user_id: shift?.user_id || defaultUserId || "",
    user_name: shift?.user_name || "",
    date: shift?.date || defaultDate || toISODate(new Date()),
    start_time: shift?.start_time || "09:00",
    end_time: shift?.end_time || "17:00",
    location_id: shift?.location_id || (locations[0]?.location_id || ""),
    role: shift?.role || "",
    notes: shift?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  // Keep user_name in sync if user picks a different staff
  const pickStaff = (uid) => {
    const s = staffList.find(x => x.user_id === uid);
    setForm(f => ({ ...f, user_id: uid, user_name: s?.name || "" }));
  };

  const save = async () => {
    if (!form.user_id) { toast.error("Pick a staff member"); return; }
    if (minutesBetween(form.start_time, form.end_time) <= 0) {
      toast.error("End time must be after start time");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/shifts", form);
        toast.success("Shift created");
        onSaved(res.data);
      } else {
        const res = await api.put(`/shifts/${shift.shift_id}`, form);
        toast.success("Shift updated");
        onSaved(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const del = async () => {
    if (!shift?.shift_id) return;
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/shifts/${shift.shift_id}`);
      toast.success("Shift deleted");
      onDeleted?.(shift.shift_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const hours = minutesBetween(form.start_time, form.end_time) / 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="shift-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">
              {isNew ? "New shift" : "Edit shift"}
            </p>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">
              {form.user_name || "Pick a staff member"}
            </h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Staff</p>
            <select value={form.user_id} onChange={e => pickStaff(e.target.value)}
              data-testid="shift-user-select"
              className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300">
              <option value="">Select staff…</option>
              {staffList.map(s => (
                <option key={s.user_id} value={s.user_id}>{s.name} {s.role ? `· ${s.role}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Date</p>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              data-testid="shift-date" className="rounded-xl border-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Start</p>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                data-testid="shift-start" className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">End</p>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                data-testid="shift-end" className="rounded-xl border-slate-200" />
            </div>
          </div>
          <p className="text-xs text-slate-500 -mt-2">
            <Clock className="w-3 h-3 inline mr-1" /> Duration: <span className="font-medium">{hours > 0 ? `${hours.toFixed(1)} hours` : "—"}</span>
          </p>
          {locations.length > 1 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Location</p>
              <select value={form.location_id || ""} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}
                data-testid="shift-location"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300">
                <option value="">No specific location</option>
                {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Role / notes</p>
            <Input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              data-testid="shift-role" placeholder="Stylist, color, training…"
              className="rounded-xl border-slate-200" />
          </div>
          {shift?.conflicts?.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="shift-conflicts">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Conflicts</p>
              <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                {shift.conflicts.map((c, i) => <li key={i}>• {c.label}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex items-center gap-3">
          {!isNew && (
            <button onClick={del} data-testid="delete-shift"
              className="text-xs text-rose-600 hover:bg-rose-50 px-3 h-9 rounded-full inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 px-3 h-10">Cancel</button>
          <Button onClick={save} disabled={saving} data-testid="save-shift"
            className="bg-slate-900 text-white rounded-full h-10 px-5 hover:bg-slate-800 disabled:opacity-50">
            <Check className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Templates modal ───────────────────────────────────────────
function TemplatesModal({ templates, staffList, onClose, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    user_id: "", user_name: "", day_of_week: 0, start_time: "09:00", end_time: "17:00",
  });

  const add = async () => {
    if (!form.user_id) { toast.error("Pick a staff member"); return; }
    try {
      await api.post("/shifts/templates", form);
      toast.success("Template added");
      setAdding(false);
      setForm({ user_id: "", user_name: "", day_of_week: 0, start_time: "09:00", end_time: "17:00" });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (tid) => {
    if (!window.confirm("Remove this template? It won't undo previously generated shifts.")) return;
    try {
      await api.delete(`/shifts/templates/${tid}`);
      toast.success("Removed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const pickStaff = (uid) => {
    const s = staffList.find(x => x.user_id === uid);
    setForm(f => ({ ...f, user_id: uid, user_name: s?.name || "" }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="templates-modal">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Recurring Schedule</p>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">Weekly templates</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500 font-['Manrope']">
            Each template generates a shift every week on its day. Use <span className="font-semibold">Generate from templates</span> on the schedule page to materialize the upcoming week.
          </p>

          <div className="bg-[#FAFAFA] rounded-xl border border-slate-200 divide-y divide-slate-200">
            {templates.length === 0 && (
              <p className="text-sm text-slate-400 italic px-4 py-6 text-center">No templates yet.</p>
            )}
            {templates.map(t => (
              <div key={t.template_id} className="px-4 py-3 flex items-center gap-3" data-testid={`template-row-${t.template_id}`}>
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 w-12">{DAY_LABELS[t.day_of_week]}</span>
                <p className="text-sm font-semibold text-slate-900 flex-1">{t.user_name || t.user_id}</p>
                <p className="text-xs text-slate-500 tabular-nums">{fmtShiftTime(t.start_time)}–{fmtShiftTime(t.end_time)}</p>
                <button onClick={() => remove(t.template_id)} data-testid={`remove-template-${t.template_id}`}
                  className="w-8 h-8 rounded-full hover:bg-rose-50 flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </button>
              </div>
            ))}
          </div>

          {!adding ? (
            <button onClick={() => setAdding(true)} data-testid="add-template-btn"
              className="w-full h-10 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-slate-300 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" /> Add template
            </button>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="template-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Staff</p>
                  <select value={form.user_id} onChange={e => pickStaff(e.target.value)}
                    data-testid="template-user"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300">
                    <option value="">Pick…</option>
                    {staffList.map(s => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Day</p>
                  <select value={form.day_of_week} onChange={e => setForm(f => ({ ...f, day_of_week: parseInt(e.target.value, 10) }))}
                    data-testid="template-dow"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300">
                    {DAY_LABELS_LONG.map((l, i) => <option key={l} value={i}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Start</p>
                  <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                    className="h-10 rounded-lg border-slate-200" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">End</p>
                  <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                    className="h-10 rounded-lg border-slate-200" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setAdding(false)} className="text-xs text-slate-500 px-3 h-8">Cancel</button>
                <Button onClick={add} data-testid="save-template" className="bg-slate-900 text-white rounded-full h-8 px-4 text-xs hover:bg-slate-800">
                  Add template
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────
export default function SchedulePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [shifts, setShifts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // shift or { defaultDate, defaultUserId } or null
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [generating, setGenerating] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const fetchAll = useCallback(async () => {
    try {
      const [shiftsRes, staffRes, locRes, tplRes] = await Promise.all([
        api.get(`/shifts?start_date=${toISODate(weekStart)}&end_date=${toISODate(weekEnd)}`),
        api.get("/staff"),
        api.get("/locations"),
        api.get("/shifts/templates"),
      ]);
      setShifts(shiftsRes.data || []);
      setStaffList((staffRes.data || []).filter(s => s.is_active !== false));
      setLocations(locRes.data || []);
      setTemplates(tplRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load schedule");
    }
    setLoading(false);
  }, [weekStart, weekEnd]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Index shifts by user_id + date for grid lookup
  const shiftsByCell = useMemo(() => {
    const map = new Map();
    for (const s of shifts) {
      const key = `${s.user_id}|${s.date}`;
      const list = map.get(key) || [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [shifts]);

  const visibleStaff = useMemo(() => {
    let arr = staffList;
    if (locationFilter !== "all") {
      arr = arr.filter(s =>
        (s.location_ids || []).length === 0 || (s.location_ids || []).includes(locationFilter)
      );
    }
    // Only show staff that either have a shift this week OR are in the filtered location
    const staffWithShift = new Set(shifts.map(s => s.user_id));
    const sortedStaff = [...arr].sort((a, b) => {
      const aHas = staffWithShift.has(a.user_id) ? 0 : 1;
      const bHas = staffWithShift.has(b.user_id) ? 0 : 1;
      if (aHas !== bHas) return aHas - bHas;
      return (a.name || "").localeCompare(b.name || "");
    });
    return sortedStaff;
  }, [staffList, locationFilter, shifts]);

  const weeklyTotals = useMemo(() => {
    const totals = { hours: 0, shifts: shifts.length, conflicts: 0 };
    for (const s of shifts) {
      totals.hours += minutesBetween(s.start_time, s.end_time);
      if ((s.conflicts || []).length > 0) totals.conflicts += 1;
    }
    totals.hours = totals.hours / 60;
    return totals;
  }, [shifts]);

  const hoursPerStaff = useMemo(() => {
    const map = new Map();
    for (const s of shifts) {
      map.set(s.user_id, (map.get(s.user_id) || 0) + minutesBetween(s.start_time, s.end_time));
    }
    return map;
  }, [shifts]);

  const copyLastWeek = async () => {
    if (!window.confirm("Copy last week's shifts into the current week? Duplicates will be skipped.")) return;
    setGenerating(true);
    try {
      const source = toISODate(addDays(weekStart, -7));
      const res = await api.post("/shifts/copy-week", { source_date: source, target_date: toISODate(weekStart) });
      toast.success(`Copied ${res.data.created} shift${res.data.created === 1 ? "" : "s"} (skipped ${res.data.skipped})`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
    setGenerating(false);
  };

  const materialize = async () => {
    if (templates.length === 0) { toast.error("No templates yet — set them up first"); setShowTemplates(true); return; }
    setGenerating(true);
    try {
      const res = await api.post("/shifts/templates/materialize", { week_start: toISODate(weekStart) });
      toast.success(`Generated ${res.data.created} shift${res.data.created === 1 ? "" : "s"} from templates (skipped ${res.data.skipped})`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed"); }
    setGenerating(false);
  };

  const onSaved = () => { setEditor(null); fetchAll(); };
  const onDeleted = () => { setEditor(null); fetchAll(); };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const today = toISODate(new Date());

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="schedule-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Schedule</h1>
          <p className="text-slate-600 text-sm mt-1">
            Build the week, manage recurring shifts, see conflicts at a glance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowTemplates(true)} variant="outline" data-testid="templates-btn"
            className="border-slate-200 rounded-full h-10 px-4 text-sm">
            <Settings className="w-4 h-4 mr-1.5" /> Templates ({templates.length})
          </Button>
          <Button onClick={copyLastWeek} disabled={generating} variant="outline" data-testid="copy-week-btn"
            className="border-slate-200 rounded-full h-10 px-4 text-sm">
            <Copy className="w-4 h-4 mr-1.5" /> Copy last week
          </Button>
          <Button onClick={materialize} disabled={generating} data-testid="generate-from-templates-btn"
            className="bg-rose-500 text-white rounded-full h-10 px-4 text-sm hover:bg-rose-600 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${generating ? "animate-spin" : ""}`} />
            Generate from templates
          </Button>
        </div>
      </div>

      {/* Toolbar: week nav + filters + totals */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="prev-week"
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="this-week"
            className="text-xs px-3 h-9 rounded-full hover:bg-slate-100 text-slate-600 font-medium">
            This week
          </button>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="next-week"
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>
        <div className="font-['Outfit'] text-base font-semibold text-slate-900" data-testid="week-label">
          {weekStart.toLocaleDateString("en-US", { month: "long", day: "numeric" })}
          {" – "}
          {weekEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </div>
        <div className="h-6 w-px bg-slate-200" />
        {locations.length > 1 && (
          <>
            <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}
              data-testid="location-filter"
              className="h-9 px-3 rounded-full border border-slate-200 text-xs bg-white text-slate-700 focus:outline-none focus:border-rose-300">
              <option value="all">All locations</option>
              {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
            <div className="h-6 w-px bg-slate-200" />
          </>
        )}
        <div className="ml-auto flex items-center gap-4 text-sm">
          <div className="text-slate-500">
            <span className="font-semibold text-slate-900 tabular-nums">{weeklyTotals.shifts}</span> shifts
          </div>
          <div className="text-slate-500">
            <span className="font-semibold text-slate-900 tabular-nums">{weeklyTotals.hours.toFixed(1)}h</span> total
          </div>
          {weeklyTotals.conflicts > 0 && (
            <div className="text-amber-600 inline-flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" /> {weeklyTotals.conflicts} conflict{weeklyTotals.conflicts === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      {/* Week grid */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="schedule-grid">
        <div className="grid border-b border-slate-200 bg-[#FAFAFA] sticky top-0 z-10"
          style={{ gridTemplateColumns: "180px repeat(7, minmax(0, 1fr))" }}>
          <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">Staff</div>
          {weekDates.map((d, i) => {
            const iso = toISODate(d);
            const isToday = iso === today;
            return (
              <div key={iso} className={`px-3 py-3 text-center border-l border-slate-200 ${isToday ? "bg-rose-50" : ""}`}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{DAY_LABELS[i]}</p>
                <p className={`font-['Outfit'] text-base ${isToday ? "font-semibold text-rose-600" : "font-medium text-slate-900"}`}>
                  {d.getDate()}
                </p>
              </div>
            );
          })}
        </div>

        {visibleStaff.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <CalendarDays className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No active staff{locationFilter !== "all" ? " at this location" : ""}</p>
          </div>
        ) : (
          visibleStaff.map((s) => {
            const totalMins = hoursPerStaff.get(s.user_id) || 0;
            return (
              <div key={s.user_id}
                className="grid border-b border-slate-100 last:border-b-0 hover:bg-[#FAFAFA] transition-colors"
                style={{ gridTemplateColumns: "180px repeat(7, minmax(0, 1fr))" }}
                data-testid={`staff-row-${s.user_id}`}>
                <div className="px-4 py-3 flex items-center gap-3 border-r border-slate-100">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-100 to-amber-100 flex items-center justify-center text-xs font-bold text-rose-600">
                    {(s.name || "?").slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{s.name}</p>
                    <p className="text-[11px] text-slate-400 tabular-nums">{(totalMins / 60).toFixed(1)}h this week</p>
                  </div>
                </div>
                {weekDates.map((d) => {
                  const iso = toISODate(d);
                  const cellShifts = shiftsByCell.get(`${s.user_id}|${iso}`) || [];
                  const isToday = iso === today;
                  return (
                    <div key={iso}
                      onClick={(e) => { if (e.target === e.currentTarget) setEditor({ defaultDate: iso, defaultUserId: s.user_id }); }}
                      className={`min-h-[72px] px-1.5 py-1.5 border-l border-slate-100 cursor-pointer relative group ${isToday ? "bg-rose-50/30" : ""}`}
                      data-testid={`cell-${s.user_id}-${iso}`}>
                      {cellShifts.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                          <Plus className="w-4 h-4 text-slate-300" />
                        </div>
                      )}
                      {cellShifts.map(sh => {
                        const hasConflict = (sh.conflicts || []).length > 0;
                        return (
                          <button key={sh.shift_id}
                            onClick={(e) => { e.stopPropagation(); setEditor(sh); }}
                            data-testid={`shift-pill-${sh.shift_id}`}
                            className={`w-full text-left rounded-lg px-2 py-1 mb-1 last:mb-0 transition-all ${
                              hasConflict
                                ? "bg-amber-50 border border-amber-300 hover:bg-amber-100"
                                : "bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
                            }`}>
                            <p className={`text-[11px] font-semibold tabular-nums ${hasConflict ? "text-amber-800" : "text-emerald-800"}`}>
                              {fmtShiftTime(sh.start_time)}–{fmtShiftTime(sh.end_time)}
                            </p>
                            <p className="text-[10px] text-slate-500 tabular-nums">
                              {fmtHours(minutesBetween(sh.start_time, sh.end_time))}
                              {sh.role && ` · ${sh.role}`}
                            </p>
                            {hasConflict && (
                              <p className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                                <AlertTriangle className="w-2.5 h-2.5" /> {(sh.conflicts || []).length}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-[11px] text-slate-500">
        <div className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-200" /> Scheduled</div>
        <div className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-300" /> Has conflict</div>
        <div className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-400" /> Click any empty cell to add a shift · Click a pill to edit</div>
      </div>

      {editor && (
        <ShiftModal
          shift={editor.shift_id ? editor : null}
          defaultDate={editor.defaultDate}
          defaultUserId={editor.defaultUserId}
          staffList={staffList}
          locations={locations}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}

      {showTemplates && (
        <TemplatesModal
          templates={templates}
          staffList={staffList}
          onClose={() => setShowTemplates(false)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
}
